import { useState, useEffect, useCallback } from 'react';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const SESSION_KEY = 'svdev07_session';

function LoginPage({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/health`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': password
        }
      });

      if (res.ok) {
        sessionStorage.setItem(SESSION_KEY, password);
        onLogin(password);
      } else {
        setError('รหัสผ่านไม่ถูกต้อง');
      }
    } catch (e) {
      setError('ไม่สามารถเชื่อมต่อ API ได้');
    }
    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-logo">
          <span className="logo-icon">⚡</span>
          <h1>svdev07 Manager</h1>
          <p>Traefik Route Manager for 10.0.1.46</p>
        </div>
        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="กรอก Password"
              required
              autoFocus
            />
          </div>
          {error && <div className="alert error">{error}</div>}
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'กำลังตรวจสอบ...' : '🔐 เข้าสู่ระบบ'}
          </button>
        </form>
        <p className="login-hint">กรุณาติดต่อ Admin หากต้องการรหัสผ่าน</p>
      </div>
    </div>
  );
}

function App() {
  const [apiKey, setApiKey] = useState(() => sessionStorage.getItem(SESSION_KEY) || '');

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setApiKey('');
  };

  if (!apiKey) {
    return <LoginPage onLogin={setApiKey} />;
  }

  return <MainApp apiKey={apiKey} onLogout={handleLogout} />;
}

function MainApp({ apiKey, onLogout }) {
  const headers = {
    'Content-Type': 'application/json',
    'x-api-key': apiKey
  };

  const [routes, setRoutes] = useState([]);
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', domain: '', port: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tab, setTab] = useState('routes');

  const fetchRoutes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/routes`, { headers });
      const data = await res.json();
      setRoutes(data);
    } catch (e) {
      setError('Cannot connect to API');
    }
  }, []);

  const fetchContainers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/containers`, { headers });
      const data = await res.json();
      setContainers(data);
    } catch (e) {
      setError('Cannot fetch containers');
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchRoutes(), fetchContainers()])
      .finally(() => setLoading(false));
  }, [fetchRoutes, fetchContainers]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${API_URL}/routes`, {
        method: 'POST',
        headers,
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSuccess(`✅ Route "${form.name}" added successfully`);
      setForm({ name: '', domain: '', port: '' });
      fetchRoutes();
    } catch (e) {
      setError(`❌ ${e.message}`);
    }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete route "${name}"?`)) return;
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${API_URL}/routes/${name}`, {
        method: 'DELETE',
        headers
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSuccess(`✅ Route "${name}" deleted`);
      fetchRoutes();
    } catch (e) {
      setError(`❌ ${e.message}`);
    }
  };

  const fillFromContainer = (container) => {
    const port = container.ports[0]?.host || '';
    const suggestedName = container.name.split('-')[0];
    setForm(f => ({
      ...f,
      name: suggestedName,
      port
    }));
    setTab('routes');
  };

  return (
    <div className="app">
      <header>
        <div className="header-content">
          <div className="logo">
            <span className="logo-icon">⚡</span>
            <div>
              <h1>svdev07 Manager</h1>
              <p>Traefik Route Manager for 10.0.1.46</p>
            </div>
          </div>
          <div className="header-right">
            <div className="server-badge">
              <span className="dot"></span>
              10.0.1.45 → 10.0.1.46
            </div>
            <button className="btn-logout" onClick={onLogout}>
              🔓 ออกจากระบบ
            </button>
          </div>
        </div>
      </header>

      <main>
        {error && <div className="alert error">{error}</div>}
        {success && <div className="alert success">{success}</div>}

        <div className="tabs">
          <button
            className={tab === 'routes' ? 'active' : ''}
            onClick={() => setTab('routes')}
          >
            🔀 Routes ({routes.length})
          </button>
          <button
            className={tab === 'containers' ? 'active' : ''}
            onClick={() => setTab('containers')}
          >
            📦 Containers on svdev07 ({containers.length})
          </button>
          <button
            className={tab === 'add' ? 'active' : ''}
            onClick={() => setTab('add')}
          >
            ➕ Add Route
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <>
            {tab === 'routes' && (
              <div className="section">
                <h2>Active Routes</h2>
                {routes.length === 0 ? (
                  <div className="empty">No routes configured yet. Add one to get started.</div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Domain</th>
                        <th>Port on svdev07</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {routes.map(route => (
                        <tr key={route.name}>
                          <td><code>{route.name}</code></td>
                          <td>
                            <a
                              href={`https://${route.domain}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {route.domain} ↗
                            </a>
                          </td>
                          <td><code>:{route.port}</code></td>
                          <td>
                            <button
                              className="btn-danger"
                              onClick={() => handleDelete(route.name)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {tab === 'containers' && (
              <div className="section">
                <h2>Containers on svdev07 (10.0.1.46)</h2>
                <p className="hint">Click "Use" to pre-fill the Add Route form</p>
                {containers.length === 0 ? (
                  <div className="empty">No containers running</div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Container</th>
                        <th>Image</th>
                        <th>Port Mapping</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {containers.map(c => (
                        <tr key={c.name}>
                          <td><code>{c.name}</code></td>
                          <td>{c.image}</td>
                          <td>
                            {c.ports.length > 0
                              ? c.ports.map(p => (
                                <span key={p.host} className="port-badge">
                                  {p.host}→{p.container}
                                </span>
                              ))
                              : <span className="no-port">No port mapped</span>
                            }
                          </td>
                          <td>
                            <span className="status-badge">
                              {c.status}
                            </span>
                          </td>
                          <td>
                            {c.ports.length > 0 && (
                              <button
                                className="btn-secondary"
                                onClick={() => fillFromContainer(c)}
                              >
                                Use
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <button className="btn-refresh" onClick={fetchContainers}>
                  🔄 Refresh
                </button>
              </div>
            )}

            {tab === 'add' && (
              <div className="section">
                <h2>Add New Route</h2>
                <div className="info-box">
                  <strong>Flow:</strong> Domain → Traefik (10.0.1.45) → Port บน svdev07 (10.0.1.46) → Container
                </div>
                <form onSubmit={handleAdd} className="form">
                  <div className="form-group">
                    <label>Route Name <span className="req">*</span></label>
                    <input
                      type="text"
                      placeholder="เช่น myapp"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      required
                    />
                    <small>ชื่อสำหรับอ้างอิงใน Traefik config</small>
                  </div>
                  <div className="form-group">
                    <label>Domain <span className="req">*</span></label>
                    <input
                      type="text"
                      placeholder="เช่น myapp.cigblusolutions.com"
                      value={form.domain}
                      onChange={e => setForm(f => ({ ...f, domain: e.target.value }))}
                      required
                    />
                    <small>Domain ที่ต้องการใช้งาน (ต้อง DNS ชี้มาที่ firewallci1.fortiddns.com แล้ว)</small>
                  </div>
                  <div className="form-group">
                    <label>Host Port บน svdev07 <span className="req">*</span></label>
                    <input
                      type="number"
                      placeholder="เช่น 8082"
                      value={form.port}
                      onChange={e => setForm(f => ({ ...f, port: e.target.value }))}
                      required
                      min="1024"
                      max="65535"
                    />
                    <small>Port ที่ตั้งใน Coolify Port Mappings (เช่น 8082:80)</small>
                  </div>
                  <button type="submit" className="btn-primary">
                    ➕ Add Route
                  </button>
                </form>

                <div className="convention">
                  <h3>Port Convention</h3>
                  <table>
                    <thead>
                      <tr><th>Port</th><th>สถานะ</th><th>Domain</th></tr>
                    </thead>
                    <tbody>
                      {[8081,8082,8083,8084,8085].map(port => {
                        const used = routes.find(r => r.port === String(port));
                        return (
                          <tr key={port}>
                            <td><code>{port}</code></td>
                            <td>
                              {used
                                ? <span className="used">ใช้งานแล้ว</span>
                                : <span className="free">ว่าง</span>
                              }
                            </td>
                            <td>{used?.domain || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default App;
